import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, getPhotosBaseUrl } from '../../services/api';
import { useUI } from '../../UIContext';
import { handleErr } from '../../utils/functions';
import './news-list.css';

const PAGE_SIZE = 4;

function NewsList() {
    const navigate = useNavigate();
    const { setLoading, setMessage } = useUI();
    const [pageLoaded, setPageLoaded] = useState(false);
    const [news, setNews] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        async function getFirstPageNews() {
            setLoading(true);

            await get(`/news/search?page=1&pageSize=${PAGE_SIZE}`, true, setMessage,
                (response) => {
                    const { loadedNews, hasMore } = parsePaginatedNewsResponse(response.data, 1);
                    setNews(loadedNews);
                    setCurrentPage(1);
                    setHasMorePages(hasMore);
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar as notícias: ${err}`);
                },
                () => {
                    setLoading(false);
                    setPageLoaded(true);
                }
            );
        }

        getFirstPageNews();
    }, [setLoading, setMessage]);

    if (!pageLoaded)
        return null;

    const firstNews = news[0];
    const secondNews = news[1];
    const initialOtherNews = news.slice(2, PAGE_SIZE);
    const loadedMoreNews = news.slice(PAGE_SIZE);

    function parsePaginatedNewsResponse(data, requestedPage) {
        const loadedNews = Array.isArray(data) ? data : data?.items || [];

        const totalPages = data?.pageTotal;
        const responsePage = data?.page;
        const effectivePage = typeof responsePage === 'number' ? responsePage : requestedPage;
        const hasNextFlag = data?.hasNext ?? data?.hasMore;

        const hasMore = typeof hasNextFlag === 'boolean'
            ? hasNextFlag
            : (typeof totalPages === 'number'
                ? effectivePage < totalPages
                : loadedNews.length === PAGE_SIZE);

        return {
            loadedNews,
            hasMore
        };
    }

    async function loadMoreNews() {
        if (isLoadingMore || !hasMorePages)
            return;

        const nextPage = currentPage + 1;
        setIsLoadingMore(true);

        await get(`/news/search?page=${nextPage}&pageSize=${PAGE_SIZE}`, true, setMessage,
            (response) => {
                const { loadedNews, hasMore } = parsePaginatedNewsResponse(response.data, nextPage);

                setNews((currentNews) => {
                    const ids = new Set(currentNews.map((item) => item.id));
                    const uniqueLoadedNews = loadedNews.filter((item) => !ids.has(item.id));
                    return [...currentNews, ...uniqueLoadedNews];
                });

                setCurrentPage(nextPage);
                setHasMorePages(hasMore);
            },
            (err) => {
                handleErr(setMessage, `Não foi possível carregar mais notícias: ${err}`);
            },
            () => {
                setIsLoadingMore(false);
            }
        );
    }

    function getNewsImage(newsItem) {
        const imagePath = newsItem?.imagePath || newsItem?.photoPath || newsItem?.coverPath;
        const photosBaseUrl = getPhotosBaseUrl();
        const fallbackFolder = (newsItem?.hasModule || newsItem?.module) ? 'modules' : 'news';

        if (!imagePath || typeof imagePath !== 'string')
            return undefined;

        const normalizedPath = imagePath.trim().replace(/\\/g, '/').replace(/^\/+/, '');

        if (!normalizedPath)
            return undefined;

        if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://'))
            return normalizedPath;

        const pathParts = normalizedPath.split('/').filter(Boolean);
        const newsIndex = pathParts.lastIndexOf('news');
        const modulesIndex = pathParts.lastIndexOf('modules');
        const folderIndex = Math.max(newsIndex, modulesIndex);

        if (folderIndex >= 0) {
            const publicFolder = pathParts[folderIndex];
            const fileName = pathParts[pathParts.length - 1];

            if (!fileName || !photosBaseUrl)
                return undefined;

            return `${photosBaseUrl.replace(/\/+$/, '')}/${publicFolder}/${fileName}`;
        }

        if (normalizedPath.includes('/')) {
            const fileName = pathParts[pathParts.length - 1];

            if (!fileName || !photosBaseUrl)
                return undefined;

            return `${photosBaseUrl.replace(/\/+$/, '')}/${fallbackFolder}/${fileName}`;
        }

        if (!photosBaseUrl)
            return undefined;

        return `${photosBaseUrl.replace(/\/+$/, '')}/${fallbackFolder}/${normalizedPath}`;
    }

    function getNewsTitle(newsItem) {
        return newsItem?.title || newsItem?.name || 'Sem título';
    }

    function onClickNews(newsItem) {
        navigate(`/news/${newsItem.id}`);
    }

    function renderNewsCard(newsItem, variant = 'default') {
        const image = getNewsImage(newsItem);
        const cardClassName =
            variant === 'featured-main'
                ? 'news-card news-card-featured-main zoom-effect'
                : variant === 'featured-side'
                    ? 'news-card news-card-featured-side zoom-effect'
                    : 'news-card zoom-effect';

        return (
            <button
                type='button'
                key={newsItem.id || `${getNewsTitle(newsItem)}-${image || 'no-image'}`}
                className={cardClassName}
                onClick={() => onClickNews(newsItem)}>
                <div
                    className='news-card-image'
                    style={{ backgroundImage: image ? `url("${image}")` : 'none' }}>
                    {!image && <span>Sem imagem</span>}
                </div>

                <h2>{getNewsTitle(newsItem)}</h2>
            </button>
        );
    }

    return (
        <main className='news-list-page'>
            <div className='glass-bg-full-page'></div>

            <h1>Notícias e Tendências</h1>
            <h3>Conteúdos e tendências para ampliar sua visão e atuação no turismo.</h3>

            <section className='news-layout'>
                <div className='news-left-column'>
                    {firstNews && renderNewsCard(firstNews, 'featured-main')}
                </div>

                <div className='news-right-column'>
                    {secondNews && renderNewsCard(secondNews, 'featured-side')}

                    <div className='news-right-list'>
                        {initialOtherNews.map((newsItem) => renderNewsCard(newsItem))}
                    </div>
                </div>
            </section>

            {loadedMoreNews.length > 0 && (
                <section className='news-extra-grid'>
                    {loadedMoreNews.map((newsItem) => renderNewsCard(newsItem))}
                </section>
            )}

            {hasMorePages && (
                <div className='news-load-more-container'>
                    <button
                        type='button'
                        className='blue-button zoom-effect news-load-more-button'
                        onClick={loadMoreNews}
                        disabled={isLoadingMore}>
                        {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
                    </button>
                </div>
            )}
        </main>
    );
}

export default NewsList;